/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import { Button as CDSButton, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ExternalLinkIcon } from '@coursera/cds-icons';

import type { EntWebsiteBlockCtaQuery, EntWebsiteCtaFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import Button from 'bundles/ent-website/contentful/core/ContentfulButton';
import Markdown from 'bundles/ent-website/contentful/core/Markdown';
import RichText from 'bundles/ent-website/contentful/core/RichText';
import { entWebsiteBlockCtaQuery } from 'bundles/ent-website/queries/EntWebsiteCta';
import DataConsumerWrapper from 'bundles/ent-website/utils/DataConsumerWrapper';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

import _t from 'i18n!nls/ent-website';

type Props = BlockModuleInnerProps<EntWebsiteCtaFragment>;

const styles: Record<string, Interpolation<Theme>> = {
  container: () => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    background: var(--cds-color-grey-975);
    padding: var(--cds-spacing-1400) var(--cds-spacing-400);
    text-align: center;
    color: var(--cds-color-white-0);
    ${breakpoints.down('xs')} {
      margin: calc(var(--cds-spacing-600) * -1) calc(var(--cds-spacing-200) * -1);
      padding: var(--cds-spacing-600) var(--cds-spacing-200);
      align-items: flex-start;
      text-align: left;
    }
  `,
  headline: () => css`
    font-size: 4rem !important;
    line-height: 100% !important;
    margin: var(--cds-spacing-150) 0 var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      font-size: 2.5rem !important;
      margin-bottom: var(--cds-spacing-300);
    }
  `,

  subhead: () => css`
    font-size: 1.5rem !important;
    line-height: 150% !important;
    margin-bottom: var(--cds-spacing-150);
    ${breakpoints.down('xs')} {
      font-size: 1rem !important;
      margin-bottom: 0;
    }
  `,
  buttons: () => css`
    margin: var(--cds-spacing-600) 0 var(--cds-spacing-400);
    display: flex;

    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-400);
      width: 100%;
    }
  `,
  button: () => css`
    margin-right: var(--cds-spacing-200);

    &:last-child {
      margin-right: 0;
    }

    a {
      ${breakpoints.down('xs')} {
        font-size: 14px;
        line-height: 20px;
        padding: 10px 16px;
      }
    }
  `,
  buttonSecondary: () => css`
    a {
      color: var(--cds-color-white-0);
      background: transparent;
      box-shadow: var(--cds-color-white-0) 0 0 0 1px inset;
    }
  `,
  subCtaText: () => css`
    p {
      font-size: 14px;
    }

    a {
      color: inherit;
      text-decoration: underline;
    }
  `,
  externalLinkIcon: () => css`
    margin-left: var(--cds-spacing-50);
  `,
};

export function CTAModule({ content, assembly = {} }: Props) {
  const { visibility } = assembly;

  if (!content) return null;

  const {
    headline,
    subhead,
    ctaButton1Text,
    ctaButton1Target,
    ctaButton2Text,
    ctaButton2Target,
    subCtaText,
    subCtaTextMarkdown,
  } = content;

  const contentPanel = () => (
    <div css={styles.container}>
      {visibility?.headline && headline && (
        <Typography color="inherit" variant="h2" css={styles.headline}>
          {headline}
        </Typography>
      )}
      {visibility?.subhead && subhead && (
        <Typography2 component="p" color="inherit" variant="bodyPrimary" css={styles.subhead}>
          {subhead}
        </Typography2>
      )}
      <div css={styles.buttons}>
        {visibility?.ctaButton1Text && visibility?.ctaButton1Target && ctaButton1Text && ctaButton1Target && (
          <span css={styles.button}>
            <DataConsumerWrapper template={ctaButton1Target}>
              {({ result, isLoading }) =>
                isLoading ? (
                  <CDSButton variant="primary" disabled>
                    {_t('Loading...')}
                  </CDSButton>
                ) : (
                  <Button variant="primary" component="a" href={result} module={content} position={0}>
                    {ctaButton1Text}
                  </Button>
                )
              }
            </DataConsumerWrapper>
          </span>
        )}
        {visibility?.ctaButton2Text && visibility?.ctaButton2Target && ctaButton2Text && ctaButton2Target && (
          <span css={[styles.button, styles.buttonSecondary]}>
            <DataConsumerWrapper template={ctaButton2Target}>
              {({ result, isLoading }) =>
                isLoading ? (
                  <CDSButton variant="secondary" disabled>
                    {_t('Loading...')}
                  </CDSButton>
                ) : (
                  <Button variant="secondary" component="a" href={result} module={content} position={1}>
                    {ctaButton2Text}
                  </Button>
                )
              }
            </DataConsumerWrapper>
          </span>
        )}
      </div>
      {visibility?.subCtaText && subCtaText && (
        <div css={styles.subCtaText}>
          <RichText
            css={styles.subCtaText}
            content={subCtaText.json}
            module={content}
            typographyColor="invertBody"
            icon={<ExternalLinkIcon size="small" css={styles.externalLinkIcon} />}
          />
        </div>
      )}
      {visibility?.subCtaTextMarkdown && subCtaTextMarkdown && (
        <div css={styles.subCtaText}>
          <DataConsumerWrapper template={subCtaTextMarkdown}>
            {({ result, isLoading }) =>
              isLoading ? (
                <Typography2 component="p" color="inherit">
                  {_t('Loading...')}
                </Typography2>
              ) : (
                <Markdown module={content} source={result} />
              )
            }
          </DataConsumerWrapper>
        </div>
      )}
    </div>
  );

  return <BlockLayout assembly={noImage(assembly)} renderContent={contentPanel} />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockCTAModule = withBlockQuery<EntWebsiteCtaFragment, EntWebsiteBlockCtaQuery>(entWebsiteBlockCtaQuery)(
  CTAModule
);
